import React, { useState } from "react";

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      title: "About VeriMED",
      items: [
        {
          question: "What is VeriMED?",
          answer:
            "VeriMED is Cameroon’s first concierge-style home health and care navigation service. We bridge distance by providing international-standard medical care at home—giving peace of mind to families living abroad who want reliable, compassionate, and competent care for their loved ones in Cameroon.",
        },
        {
          question: "Who do you serve?",
          answer:
            "We serve older adults, chronically ill patients, and recovering clients in Cameroon—especially those whose children or responsible parties live abroad in the USA, UK, Canada, or Europe.",
        },
        {
          question:
            "What makes VeriMED different from a traditional clinic or home care agency?",
          answer:
            "We combine virtual care, in-home physician and nurse visits, and care navigation under one umbrella. Each client is assigned a care manager who coordinates services, provides regular updates to families, and ensures that global standards of care are met.",
        },
      ],
    },
    {
      title: "Services & Membership",
      items: [
        {
          question: "What services does VeriMED offer?",
          answer: (
            <>
              Our services include:
              <ul style={{ marginTop: 8 }}>
                <li>Physician home and virtual consultations</li>
                <li>Skilled nurse visits and wellness checks</li>
                <li>IV therapy and rehydration at home</li>
                <li>Live-in care family wellness nurse service</li>
                <li>Diabetes education and chronic disease management</li>
                <li>Post-surgery recovery and hospital-at-home programs</li>
                <li>
                  Concierge membership plans for ongoing family communication
                  and peace of mind
                </li>
              </ul>
            </>
          ),
        },
        {
          question: "How do I choose the right membership plan?",
          answer:
            "We offer four membership tiers—Essential, Premium, Elite, and Platinum—based on your loved one’s level of need. Each plan includes a dedicated care manager, family updates, and prioritized scheduling. If you’re unsure, our team can recommend a plan or provide a one-month trial.",
        },
        {
          question:
            "Can families living abroad pay directly for their parents’ care?",
          answer:
            "Yes. Our system is designed for diaspora sponsors. You can pay securely online, receive itemized receipts, and get monthly or weekly reports, depending on your plan.",
        },
      ],
    },
    {
      title: "Communication & Family Updates",
      items: [
        {
          question:
            "How will I receive updates about my loved one’s condition?",
          answer: (
            <>
              Depending on your membership level, you’ll receive:
              <ul style={{ marginTop: 8 }}>
                <li>Monthly or weekly WhatsApp or email updates</li>
                <li>
                  Short video summaries from your physician or nurse (optional
                  add-on)
                </li>
                <li>Secure virtual meetings when important changes occur</li>
              </ul>
            </>
          ),
        },
        {
          question: "What happens if I can’t reach the team right away?",
          answer:
            "While we strive to respond within 4 hours during business days, there may be brief delays due to local power or internet interruptions. In those cases, your care manager will contact you as soon as service is restored. Emergency numbers remain available for urgent medical concerns.",
        },
      ],
    },
    {
      title: "Reliability & Response Time",
      items: [
        {
          question:
            "How do you manage delays caused by power cuts or slow internet?",
          answer:
            "We operate from multiple network providers and maintain backup communication systems. When connectivity issues occur, updates are sent through the fastest available method (WhatsApp, SMS, or phone call). Families are notified once full service resumes.",
        },
        {
          question:
            "How quickly can I expect a home visit after making a request?",
          answer:
            "For scheduled care, visits are confirmed within 48 business hours. For urgent same-day requests, we add a small emergency fee and dispatch a physician or nurse as soon as possible—typically within 4–6 hours, depending on location, access, and road conditions.",
        },
      ],
    },
    {
      title: "Clinical & Quality Standards",
      items: [
        {
          question: "Are VeriMED clinicians licensed and trained?",
          answer:
            "Absolutely. All our physicians and nurses are licensed in Cameroon and undergo ongoing training to meet international care standards in line with our W.E.C.A.R.E. values—World-Class Standards, Empathy-Driven Service, and Clinical Competence.",
        },
        {
          question: "How does VeriMED ensure care quality and safety?",
          answer: (
            <>
              We track key performance indicators such as:
              <ul style={{ marginTop: 8 }}>
                <li>Response time under 4 hours</li>
                <li>98% completion of visit documentation</li>
                <li>90% of patients with up-to-date vitals</li>
                <li>95% of alerts addressed within 4 hours</li>
              </ul>
              We also perform periodic case reviews and family satisfaction
              surveys.
            </>
          ),
        },
      ],
    },
    {
      title: "Payments & Billing",
      items: [
        {
          question: "How do I make payments?",
          answer:
            "Payments can be made online through our secured website. We provide receipts suitable for insurance reimbursement.",
        },
        {
          question: "Are medications and lab tests included in the visit fees?",
          answer:
            "No. Medications and labs are billed separately with itemized receipts from our pharmacy or lab partners.",
        },
        {
          question:
            "Is there an additional charge for after-hours or weekend visits?",
          answer:
            "Yes. Visits after 4 PM, on weekends, or holidays incur an after-hours fee of 35,000 CFA.",
        },
      ],
    },
    {
      title: "Technology & Access",
      items: [
        {
          question: "Do you have a patient app?",
          answer:
            "Yes, we are gradually rolling out our VeriMED Connect App, allowing families to view visit summaries and lab reports securely. Our clients can access clinical information through our secured patient and client portal.",
        },
        {
          question: "What if my parent lives outside the main city?",
          answer:
            "We currently serve Buea, Kumba, Limbe, Tiko, and Mutengene for in-person physician visits but offer virtual services for patients living outside of the current service area. Visits outside city limits may include a small travel surcharge depending on distance and road accessibility.",
        },
      ],
    },
    {
      title: "Peace of Mind",
      items: [
        {
          question: "What if my loved one has an emergency?",
          answer:
            "Our care teams provide Emergency Coordination Services—we can arrange urgent visits, coordinate transport to a hospital if needed, and notify the family sponsor immediately. For Elite and Platinum members, this service is included in their plan.",
        },
        {
          question: "How does VeriMED support families emotionally?",
          answer:
            "Beyond medical care, we offer empathy-driven communication. Our nurses often check in with families abroad to discuss progress, challenges, and next steps—because we believe peace of mind is part of healthcare.",
        },
      ],
    },
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="faq_container">
      <div className="faq_inner">
        <h2 className="faq_main_title">Frequently Asked Questions</h2>

        {faqs.map((section, sectionIndex) => (
          <div key={sectionIndex} className="faq_section">
            <h2 className="faq_section_title">{section.title}</h2>

            {section.items.map((faq, index) => {
              const currentIndex = `${sectionIndex}-${index}`;

              return (
                <div key={currentIndex} className="faq_item">
                  <div
                    className="faq_question_row"
                    onClick={() => toggleFAQ(currentIndex)}
                  >
                    <h3 className="faq_question">{faq.question}</h3>
                    <span className="faq_icon">
                      {openIndex === currentIndex ? "−" : "+"}
                    </span>
                  </div>

                  {openIndex === currentIndex && (
                    <div className="faq_answer">{faq.answer}</div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FAQ;
